import styled from '@emotion/styled';
import { Stack } from '@hotjar/hotsauce';
import { LanguageSelector } from '@hotjar/feedback/components/common/Wizard/LanguageSelector';
import { BrandingToggle } from '@hotjar/feedback/components/common/Wizard/BrandingToggle';
import type { LanguageSelectorProps } from '@hotjar/feedback/components/common/Wizard/LanguageSelector';
import type { BrandingToggleProps } from '@hotjar/feedback/components/common/Wizard/BrandingToggle';
import { BackgroundColorPalette, ButtonColorPalette, FontColorPalette } from './ColorPalette';
import { SurveyPosition } from './Position';
import { LogoSection } from './Logo';
import type { ColorPaletteProps } from './ColorPalette';
import type { SurveyPositionProps } from './Position';
import type { LogoSectionProps } from './Logo';

const SectionContainer = styled(Stack)``;

export type AppearanceSectionProps = Omit<LanguageSelectorProps, 'type'> &
  SurveyPositionProps &
  Omit<LogoSectionProps, 'onChange' | 'error'> &
  Pick<BrandingToggleProps, 'hideBranding'> & {
    backgroundColor: ColorPaletteProps['value'];
    onChangeBackgroundColor: ColorPaletteProps['onChange'];
    onPreviewBackgroundColor: ColorPaletteProps['onPreviewColor'];
    buttonColor: ColorPaletteProps['value'];
    onChangeButtonColor: ColorPaletteProps['onChange'];
    onPreviewButtonColor: ColorPaletteProps['onPreviewColor'];
    fontColor: ColorPaletteProps['value'];
    onChangeFontColor: ColorPaletteProps['onChange'];
    onPreviewFontColor: ColorPaletteProps['onPreviewColor'];
    showFontColor: ColorPaletteProps['showColorPalette'];
    onChangeLogo: LogoSectionProps['onChange'];
    logoError: LogoSectionProps['error'];
    onChangeBranding: BrandingToggleProps['onChange'];
  };

export const AppearanceSection = ({
  language,
  onLanguageChanged,
  backgroundColor,
  onChangeBackgroundColor,
  onPreviewBackgroundColor,
  buttonColor,
  onChangeButtonColor,
  onPreviewButtonColor,
  fontColor,
  onChangeFontColor,
  onPreviewFontColor,
  showFontColor,
  showSurveyPosition,
  position,
  positionOptions,
  onPositionChange,
  logoSource,
  logoError,
  onChangeLogo,
  onRemoveLogo,
  hideBranding,
  onChangeBranding,
}: AppearanceSectionProps): JSX.Element => {
  const languageProps = { language, onLanguageChanged };
  const positionProps = { showSurveyPosition, position, positionOptions, onPositionChange };
  const logoProps = {
    logoSource,
    error: logoError,
    onChange: onChangeLogo,
    onRemoveLogo,
  };
  const brandingProps = { hideBranding, onChange: onChangeBranding, isLogoSet: !!logoSource };
  return (
    <SectionContainer direction="column" spacing="x4pt5">
      <LanguageSelector type="survey" {...languageProps} />
      <BackgroundColorPalette
        value={backgroundColor}
        onChange={onChangeBackgroundColor}
        onPreviewColor={onPreviewBackgroundColor}
      />
      <ButtonColorPalette
        value={buttonColor}
        onChange={onChangeButtonColor}
        onPreviewColor={onPreviewButtonColor}
      />
      <FontColorPalette
        value={fontColor}
        onChange={onChangeFontColor}
        onPreviewColor={onPreviewFontColor}
        showColorPalette={showFontColor}
      />
      <SurveyPosition {...positionProps} />
      <LogoSection {...logoProps} />
      <BrandingToggle {...brandingProps} />
    </SectionContainer>
  );
};
